import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ApiError,
  getPublicHealth,
  login,
  oidcStartUrl,
  qk,
  resendVerification,
} from '../api.js';
import { FORGOT_PATH, REGISTER_PATH, safeReturnPath } from '../lib/auth.js';
import GirisKabugu, {
  AnimateIn,
  AlanEtiketi,
  HataSeridi,
  IKINCIL_BUTON,
  KartBaslik,
  MarkaButonu,
  MetinAlani,
  ParolaAlani,
  UST_HAP,
} from '../components/GirisKabugu.js';

/**
 * §27.7 — `/giris`. GÖRSEL DİL masaüstündeki referans admin panelinin login
 * ekranıyla birebirdir (`GirisKabugu`): iki sütunlu düzen (solda çıplak form, sağda
 * hero paneli), 24px grotesk başlık, 40px alanlar, göz simgeli parola, kırmızı hata
 * şeridi, üst çubukta koşullu "Kayıt ol" hapı.
 *
 * DAVRANIŞ DEĞİŞMEDİ: tüm mantık, `data-testid`ler ve kullanıcı metinleri önceki
 * sürümle aynıdır — e2e/DOM paketleri onlara kilitlidir. Bu dosyadaki değişiklik
 * yalnız sunumdur.
 *
 * Kayıt bağlantısı KOŞULLUDUR (§72.5/7): kök `/health` `signup` boolesini bildirir,
 * bağlantı YALNIZ açıkken çizilir. §43.1 ile çelişmez — kapalıyken hiçbir şey
 * söylenmez, açıkken saklanacak bir şey yoktur.
 *
 * §43.2 EKİ: doğrulanmamış hesapta (403 `email_unverified`) hata satırının altında
 * "doğrulama e-postasını yeniden gönder" düğmesi çıkar — girişi kilitleyen durumun
 * çözümü, kilidin gösterildiği yerde durmalıdır.
 */

/**
 * §27.4 login hata sözleşmesi DURUM KODUNDAN okunur:
 *   401 `invalid_credentials` — yanlış e-posta ve yanlış parola AYNI yanıttır, bu
 *        yüzden panel de "hangisi yanlış" DEMEZ (hesap keşfini engelle).
 *   429 `too_many_attempts` — 5 deneme / 15 dk (§27.4); kullanıcıya beklemesi söylenir.
 */
function loginErrorText(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    /*
     * §46.4 — `enforced` org'unda parola girişi reddedilir. Durum 401'dir ama mesaj
     * `invalid_credentials`ten AYRIDIR: burada bir bilgi saklanmıyor (parola doğru
     * olabilir, kural org'un kendi kararıdır) ve kullanıcıyı "parolan yanlış" diye
     * çözümsüz bırakmak, onu şifre sıfırlama turuna sokardı.
     */
    if (isSsoRequired(error)) return 'Bu organizasyon kurumsal giriş kullanıyor.';
    if (error.status === 401) return 'E-posta veya parola hatalı.';
    if (isUnverified(error)) return 'E-posta adresiniz henüz doğrulanmadı.';
    if (error.status === 429) return 'Çok fazla deneme yapıldı — 15 dakika sonra tekrar deneyin.';
    if (error.status === 0) return 'Sunucuya ulaşılamadı — bağlantınızı kontrol edin.';
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

/**
 * §43.2 — "doğrulanmamış kullanıcı GİRİŞ YAPAMAZ": parola DOĞRUDUR ama sunucu
 * 403 `email_unverified` döner ve panel kullanıcıyı yeniden gönderme akışına
 * taşımak zorundadır.
 *
 * Ayrım KOD üzerinden yapılır çünkü 403 tek bir sebep değildir (§27.3 `csrf_required`
 * de 403'tür) ve "doğrulama e-postası gönder" düğmesini yanlış hatada göstermek
 * kullanıcıyı çalışmayan bir çözüme sürüklerdi. Kodsuz (gövdesiz) bir 403'te ise
 * giriş ucunun ürettiği TEK 403 budur — o durumda duruma göre karar verilir.
 */
function isUnverified(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 403) return false;
  return error.code === null || error.code === 'email_unverified';
}

/**
 * §46.4 — sunucunun "kurumsal giriş kullanın" sinyali (`401 sso_required`).
 *
 * KOD üzerinden ayrılır, metinden değil: 401'in diğer hâli `invalid_credentials`tır ve
 * ikisini karıştırmak, parolası yanlış olan birine SSO düğmesi göstermek (ya da SSO'lu
 * bir kullanıcıyı parola turuna sokmak) demektir.
 */
function isSsoRequired(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401 && error.code === 'sso_required';
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  /*
   * §72.5/7 — self-serve kayıt açık mı? Kök `/health`ten okunur (muafiyet listesi).
   *
   * `useQuery` DEĞİL düz efekt: bu sorgu giriş formunun çalışmasının ÖN KOŞULU
   * değildir ve sonucu bir bağlantıyı çizip çizmemekten ibarettir. Hata durumunda
   * `null` döner (istemci yutar) ve bağlantı çizilmez — giriş ekranı, ikincil bir
   * kolaylık uğruna hata kutusu göstermez.
   */
  const [signupOpen, setSignupOpen] = useState(false);
  useEffect(() => {
    const iptal = new AbortController();
    void getPublicHealth(iptal.signal).then((h) => {
      if (h !== null) setSignupOpen(h.signup === true);
    });
    return () => iptal.abort();
  }, []);

  // Guard 401'de `state.from` bırakır (§27.7 derin bağlantı) — yoksa panel ana sayfası.
  const from = safeReturnPath((location.state as { from?: unknown } | null)?.from);

  const submit = useMutation({
    mutationFn: () => login(email.trim().toLowerCase(), password),
    onSuccess: async () => {
      /*
       * Oturum DEĞİŞTİ: eski kullanıcının önbelleği (üye listesi, ayarlar…) yeni
       * kullanıcıya sızmamalı. `clear()` tüm anahtarları atar, `me` yeniden çekilir.
       */
      queryClient.clear();
      await queryClient.invalidateQueries({ queryKey: qk.me() });
      navigate(from, { replace: true });
    },
  });

  /*
   * §43.2 — doğrulama mektubunu yeniden kuyruğa koyar. Adres formdaki adrestir:
   * kullanıcıya "adresini bir daha yaz" dedirtmek, yanlış adrese mektup göndermenin
   * en kolay yoludur. Yanıt bilinmeyen adres için de 200'dür (hesap keşfi koruması),
   * bu yüzden panel "hesabınız bulundu" DEMEZ.
   */
  const resend = useMutation({
    mutationFn: () => resendVerification(email.trim().toLowerCase()),
  });

  const error = loginErrorText(submit.error);
  const unverified = isUnverified(submit.error);
  const ssoRequired = isSsoRequired(submit.error);

  /*
   * §46.3/1 — "alan adı `org_sso.email_domain` ile eşleşirse düğme belirir".
   *
   * ŞARTNAME DÜZELTMESİ (uygulama bulgusu): eşleşmeyi PANELİN bilmesi, "bu alan adının
   * kurumsal girişi var mı?" sorusuna oturumsuz cevap veren bir uç gerektirir — yani
   * hangi şirketlerin SSO kullandığını dışarıya sayan bir ENUMERATION ORACLE'ı.
   * §46.8 tam da bu yüzden `start` ucunda "org yok" ile "SSO yok"u AYNI 404'e
   * eşitlemişti; panel için ayrı bir sızıntı açmak o kararı geçersiz kılardı.
   *
   * UYGULANAN: düğme, e-postada bir alan adı yazılır yazılmaz görünür (eşleşmeyi
   * SUNUCU bilir) ve §46.4 sinyali geldiğinde BİRİNCİL eyleme yükselir. Yapılandırma
   * yoksa uç 404 döner — yanlış bir kapıyı çalmanın bedeli budur ve bilgi sızdırmaz.
   */
  const ssoUrl = oidcStartUrl(email);
  const resendError =
    resend.error instanceof ApiError
      ? resend.error.status === 429
        ? 'Bu ağdan çok fazla istek geldi — bir süre sonra tekrar deneyin.'
        : resend.error.message
      : resend.error instanceof Error
        ? resend.error.message
        : null;

  // §43.2 — `/dogrula` başarıda buraya `state.verified` ile gelir (tek satır olumlu not).
  const verified = (location.state as { verified?: unknown } | null)?.verified === true;

  const disabled = email.trim() === '' || password === '' || submit.isPending;

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (disabled) return;
    submit.mutate();
  }

  return (
    <GirisKabugu
      testId="giris-sayfasi"
      duzen="ortala"
      ustSag={
        /* §72.5/7 — referansın "Don't have an account? Sign up" hapı; YALNIZ açıkken. */
        signupOpen ? (
          <span data-testid="giris-kayit-link" className="flex items-center gap-3">
            <span className="font-grotesk text-[13px] text-[#686868]">Hesabınız yok mu?</span>
            <Link to={REGISTER_PATH} className={UST_HAP}>
              Hesap oluşturun
            </Link>
          </span>
        ) : (
          <span className="font-grotesk text-[13px] text-[#686868]">
            Erişiminiz yoksa proje sahibinden davet isteyin.
          </span>
        )
      }
    >
      <form className="flex flex-col gap-6" onSubmit={onSubmit} noValidate>
        <AnimateIn delay={0}>
          <KartBaslik>Panele giriş</KartBaslik>
        </AnimateIn>

        <AnimateIn delay={60} className="flex flex-col gap-4">
          {/* E-posta */}
          <div className="flex flex-col gap-1">
            <AlanEtiketi>E-posta</AlanEtiketi>
            <MetinAlani
              data-testid="giris-eposta"
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="ad.soyad@sirket.com"
              maxLength={254}
            />
          </div>

          {/* Parola — referansta "Forgot your password?" etiketin SAĞINDA durur. */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <AlanEtiketi>Parola</AlanEtiketi>
              {/*
                §73.3 — "Parolamı unuttum" bayraktan BAĞIMSIZ çizilir: davetle gelmiş
                kullanıcının da parolası unutulur ve onun kayıt akışıyla ilgisi yoktur.
              */}
              <Link
                data-testid="giris-parola-unuttum"
                to={FORGOT_PATH}
                className="font-grotesk text-[12px] font-normal text-brand hover:underline"
              >
                Parolamı unuttum
              </Link>
            </div>
            <ParolaAlani
              data-testid="giris-parola"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••••"
            />
          </div>

          <MarkaButonu type="submit" data-testid="giris-gonder" disabled={disabled}>
            {submit.isPending ? 'Giriş yapılıyor…' : 'Giriş'}
          </MarkaButonu>

          {/* Hata/olumlu not — referans hata şeridi dili. */}
          {error !== null ? (
            <HataSeridi testId="giris-hata">{error}</HataSeridi>
          ) : verified ? (
            <p data-testid="giris-dogrulandi" className="font-grotesk text-[12px] text-emerald-700">
              E-posta adresiniz doğrulandı — şimdi giriş yapabilirsiniz.
            </p>
          ) : null}

          {/*
            §43.2 — doğrulanmamış hesapta hatanın YANINDA çıkış yolu da verilir; düğme
            formu göndermez (`type="button"`).
          */}
          {unverified ? (
            <div data-testid="giris-dogrulama" className="flex flex-col gap-1">
              <button
                type="button"
                data-testid="giris-dogrulama-gonder"
                disabled={resend.isPending || email.trim() === ''}
                onClick={() => resend.mutate()}
                className={`${IKINCIL_BUTON} disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {resend.isPending ? 'Gönderiliyor…' : 'Doğrulama e-postasını yeniden gönder'}
              </button>
              {resendError !== null ? (
                <p role="alert" className="font-grotesk text-[11px] text-red-600">
                  {resendError}
                </p>
              ) : resend.isSuccess ? (
                <p
                  data-testid="giris-dogrulama-gonderildi"
                  className="font-grotesk text-[11px] text-[#686868]"
                >
                  Doğrulama e-postası kuyruğa alındı — gelen kutunuzu kontrol edin.
                </p>
              ) : null}
            </div>
          ) : null}
        </AnimateIn>

        {/*
          §46.3/1 — kurumsal giriş, referansın "Or login with" + Google düzeninde.
          `<a>`dır, `fetch` DEĞİL: uç IdP'ye 302 döner ve tarayıcının gerçekten oraya
          gitmesi gerekir (§46.8). `sso_required` geldiğinde BİRİNCİL eyleme yükselir.
        */}
        {ssoUrl !== null ? (
          <>
            <AnimateIn delay={120} className="flex items-center justify-center">
              <p className="font-grotesk text-[12px] text-[#686868]">veya</p>
            </AnimateIn>
            <AnimateIn delay={160}>
              <a
                data-testid="giris-kurumsal"
                href={ssoUrl}
                className={
                  ssoRequired
                    ? 'flex h-10 w-full items-center justify-center rounded-lg border border-brand bg-white font-grotesk text-[12px] font-medium text-brand transition-colors duration-150 hover:bg-brand/5'
                    : IKINCIL_BUTON
                }
              >
                Kurumsal giriş
              </a>
            </AnimateIn>
          </>
        ) : null}
      </form>
    </GirisKabugu>
  );
}
